/**
 * Mg Theme — Wishlist (localStorage)
 * (c) 2026 NAMY SOLUTIONS LLC
 *
 * Three components:
 *   MgWishlistManager  — utility object (not a custom element)
 *   MgWishlistButton   — <mg-wishlist-button> toggle heart
 *   MgWishlistDrawer   — <mg-wishlist-drawer> slide-out panel
 */

/* ----------------------------------------------------------------
   Manager — CRUD for localStorage wishlist
   ---------------------------------------------------------------- */

var MgWishlistManager = {
  KEY: 'namy_wishlist',

  getItems: function () {
    try {
      return JSON.parse(localStorage.getItem(this.KEY)) || [];
    } catch (e) {
      return [];
    }
  },

  setItems: function (items) {
    localStorage.setItem(this.KEY, JSON.stringify(items));
    if (window.MgEvents) {
      MgEvents.publish(MgEvents.Events.WISHLIST_UPDATED, { items: items, count: items.length });
    }
  },

  addItem: function (data) {
    var items = this.getItems();
    if (items.some(function (i) { return i.productId === data.productId; })) return;
    items.push(data);
    this.setItems(items);
  },

  removeItem: function (productId) {
    var items = this.getItems().filter(function (i) { return i.productId !== productId; });
    this.setItems(items);
  },

  hasItem: function (productId) {
    return this.getItems().some(function (i) { return i.productId === productId; });
  },

  getCount: function () {
    return this.getItems().length;
  }
};

window.MgWishlistManager = MgWishlistManager;

/* ----------------------------------------------------------------
   Button — <mg-wishlist-button>
   ---------------------------------------------------------------- */

if (!customElements.get('mg-wishlist-button')) {
  class MgWishlistButton extends HTMLElement {
    connectedCallback() {
      this._productId = this.dataset.productId;
      if (!this._productId) return;

      /* custom element a11y: ARIA prohibits aria-label without role.
         role=button + tabindex=0 + keydown handler = native button equivalent. */
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

      this._syncState();
      this.addEventListener('click', this._handleClick.bind(this));
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._handleClick(e);
        }
      }.bind(this));

      this._onWishlistUpdate = this._syncState.bind(this);
      if (window.MgEvents) {
        MgEvents.subscribe(MgEvents.Events.WISHLIST_UPDATED, this._onWishlistUpdate);
      }
    }

    disconnectedCallback() {
      if (window.MgEvents && this._onWishlistUpdate) {
        MgEvents.unsubscribe(MgEvents.Events.WISHLIST_UPDATED, this._onWishlistUpdate);
      }
    }

    _syncState() {
      var active = MgWishlistManager.hasItem(this._productId);
      this.classList.toggle('is-active', active);
      var label = active
        ? (this.dataset.labelRemove || 'Remove from wishlist')
        : (this.dataset.labelAdd || 'Add to wishlist');
      this.setAttribute('aria-label', label);
    }

    _handleClick(e) {
      e.preventDefault();
      e.stopPropagation();

      var isActive = MgWishlistManager.hasItem(this._productId);

      if (isActive) {
        MgWishlistManager.removeItem(this._productId);
        if (window.MgToast) {
          MgToast.show(this.dataset.toastRemoved || 'Removed from wishlist');
        }
      } else {
        MgWishlistManager.addItem({
          productId: this._productId,
          title: this.dataset.productTitle || '',
          handle: this.dataset.productHandle || '',
          url: this.dataset.productUrl || '',
          image: this.dataset.productImage || ''
        });
        if (window.MgToast) {
          MgToast.show(this.dataset.toastAdded || 'Added to wishlist');
        }
        // D6 — heart pop on add only (CSS gates the animation; one-shot keyframe = inert at rest)
        Mg.replayAnimation(this, 'mg-wishlist-btn--pop');
      }
    }
  }

  customElements.define('mg-wishlist-button', MgWishlistButton);
}

/* ----------------------------------------------------------------
   Drawer — <mg-wishlist-drawer>
   ---------------------------------------------------------------- */

if (!customElements.get('mg-wishlist-drawer')) {
  class MgWishlistDrawer extends HTMLElement {
    connectedCallback() {
      this._dialog = this.querySelector('[data-wishlist-drawer]');
      this._body = this.querySelector('[data-wishlist-drawer-items]');
      this._empty = this.querySelector('[data-wishlist-drawer-empty]');
      this._countEl = this.querySelector('[data-wishlist-drawer-count]');
      this._template = this.querySelector('[data-wishlist-item-template]');

      if (!this._dialog) return;

      /* Open trigger (header icon) */
      this._onToggleClick = this.open.bind(this);
      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        btn.addEventListener('click', this._onToggleClick);
      }.bind(this));

      /* Close triggers */
      this.querySelectorAll('[data-wishlist-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', this.close.bind(this));
      }.bind(this));

      /* Backdrop click */
      this._dialog.addEventListener('click', function (e) {
        if (e.target === this._dialog) this.close();
      }.bind(this));

      /* Sync header count on update */
      this._onUpdate = this._updateHeaderCount.bind(this);
      if (window.MgEvents) {
        MgEvents.subscribe(MgEvents.Events.WISHLIST_UPDATED, this._onUpdate);
      }

      /* Initial header count */
      this._updateHeaderCount();
    }

    disconnectedCallback() {
      if (window.MgEvents && this._onUpdate) {
        MgEvents.unsubscribe(MgEvents.Events.WISHLIST_UPDATED, this._onUpdate);
      }
      if (this._onToggleClick) {
        document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
          btn.removeEventListener('click', this._onToggleClick);
        }.bind(this));
      }
    }

    open() {
      this._render();
      this._dialog.showModal();
    }

    close() {
      this._dialog.close();
    }

    _render() {
      if (!this._body || !this._template) return;

      var items = MgWishlistManager.getItems();
      this._body.innerHTML = '';

      if (this._countEl) this._countEl.textContent = items.length;

      var hasItems = items.length > 0;
      this._body.style.display = hasItems ? '' : 'none';
      this._empty.style.display = hasItems ? 'none' : '';

      if (!hasItems) return;

      var self = this;
      items.forEach(function (item) {
        var clone = self._template.content.cloneNode(true);
        var row = clone.querySelector('[data-wishlist-item]');

        /* Populate links */
        clone.querySelectorAll('[data-wishlist-item-link]').forEach(function (a) {
          a.href = item.url;
        });

        /* Populate image */
        var img = clone.querySelector('[data-wishlist-item-image]');
        if (img) {
          if (item.image) {
            img.src = item.image;
            img.alt = item.title;
          } else {
            img.style.display = 'none';
          }
        }

        /* Populate title */
        var titleEl = clone.querySelector('[data-wishlist-item-title]');
        if (titleEl) titleEl.textContent = item.title;

        /* Remove button */
        var removeBtn = clone.querySelector('[data-wishlist-item-remove]');
        if (removeBtn) {
          removeBtn.addEventListener('click', function () {
            MgWishlistManager.removeItem(item.productId);
            if (row && row.parentNode) row.remove();
            self._afterRemove();
          });
        }

        self._body.appendChild(clone);
      });
    }

    _afterRemove() {
      var items = MgWishlistManager.getItems();
      if (this._countEl) this._countEl.textContent = items.length;
      if (items.length === 0) {
        this._body.style.display = 'none';
        this._empty.style.display = '';
      }
    }

    _updateHeaderCount() {
      var count = MgWishlistManager.getCount();
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count > 0 ? count : '';
      });
    }
  }

  customElements.define('mg-wishlist-drawer', MgWishlistDrawer);
}
